/**
 * features/discipline/DisciplineModule.tsx
 * ------------------------------------------------------------------
 * Step 2 — Discipline Module.
 *
 * Structured discipline records per student. Each incident has a
 * category, severity, description, action taken, and status.
 * Designed to feed Student Profile Tab 3.
 *
 * Permission: `canManageDiscipline` — admin, headteacher, hoy,
 * classTeacher (contextually scoped to own class by the UI).
 *
 * Records are stored per-incident in `reportDiscipline`.
 * ------------------------------------------------------------------
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  orderBy,
} from 'firebase/firestore';
import {
  ShieldAlert,
  Plus,
  Search,
  Pencil,
  Trash2,
  Save,
  ChevronLeft,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Clock,
  ArrowUpCircle,
  Calendar,
  User,
  X,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { getLinkedTeacher } from '@/lib/permissions';
import { sanitizeComment, stripUndefined } from '@/lib/sanitize';
import { auditFromContext } from '@/lib/audit';
import StudentAvatar from '@/components/ui/StudentAvatar';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import {
  GradientHero,
  SoftCard,
  StatChip,
  AccentButton,
  glassFieldClass,
} from '@/components/ui/glass';
import type {
  DisciplineRecord,
  DisciplineCategory,
  DisciplineSeverity,
  DisciplineStatus,
} from '@/types';

/* ==================================================================
   CONSTANTS
   ================================================================== */

const CATEGORIES: { value: DisciplineCategory; label: string }[] = [
  { value: 'lateness', label: 'Lateness' },
  { value: 'uniform', label: 'Uniform Violation' },
  { value: 'bullying', label: 'Bullying' },
  { value: 'fighting', label: 'Fighting' },
  { value: 'academic_misconduct', label: 'Academic Misconduct' },
  { value: 'disrespect', label: 'Disrespect' },
  { value: 'absenteeism', label: 'Absenteeism' },
  { value: 'substance_abuse', label: 'Substance Abuse' },
  { value: 'vandalism', label: 'Vandalism' },
  { value: 'other', label: 'Other' },
];

const SEVERITIES: { value: DisciplineSeverity; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  { value: 'high', label: 'High', color: 'bg-orange-50 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  { value: 'critical', label: 'Critical', color: 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
];

const STATUSES: { value: DisciplineStatus; label: string; icon: typeof Clock }[] = [
  { value: 'open', label: 'Open', icon: Clock },
  { value: 'resolved', label: 'Resolved', icon: CheckCircle2 },
  { value: 'escalated', label: 'Escalated', icon: ArrowUpCircle },
];

function categoryLabel(c: DisciplineCategory) {
  return CATEGORIES.find((x) => x.value === c)?.label ?? c;
}

function severityInfo(s: DisciplineSeverity) {
  return SEVERITIES.find((x) => x.value === s) ?? SEVERITIES[0];
}

interface FormState {
  studentId: string;
  category: DisciplineCategory;
  severity: DisciplineSeverity;
  description: string;
  actionTaken: string;
  incidentDate: string;
  status: DisciplineStatus;
  parentNotified: boolean;
  notes: string;
}

const EMPTY_FORM: FormState = {
  studentId: '',
  category: 'lateness',
  severity: 'low',
  description: '',
  actionTaken: '',
  incidentDate: new Date().toISOString().slice(0, 10),
  status: 'open',
  parentNotified: false,
  notes: '',
};

/* ==================================================================
   COMPONENT
   ================================================================== */

export default function DisciplineModule() {
  const { students, classes, teachers, loading: dataLoading } = useData();
  const { userData, userRole } = useAuth();

  // Records loaded from Firestore
  const [records, setRecords] = useState<DisciplineRecord[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(true);

  // Mode
  const [mode, setMode] = useState<'list' | 'form'>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({ show: false, status: 'saving', message: '' });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Access: admin/headteacher see all classes; classTeacher/hoy see their own
  const PRIVILEGED = ['admin', 'headteacher'];
  const isPrivileged = PRIVILEGED.includes(userRole ?? '');

  const myClasses = useMemo(() => {
    if (isPrivileged) return classes;
    const t = getLinkedTeacher(userData, teachers);
    if (!t) return [];
    const ids = new Set<string>();
    if (t.classTeacherOf) ids.add(t.classTeacherOf);
    t.assistantClassTeacherOf?.forEach((c) => ids.add(c));
    // HOY sees classes in their forms
    if (userRole === 'hoy' && userData?.hoyForms?.length) {
      classes.forEach((c) => {
        if (userData!.hoyForms!.includes(String(c.form))) ids.add(c.id);
      });
    }
    return classes.filter((c) => ids.has(c.id));
  }, [classes, userData, teachers, userRole, isPrivileged]);

  const myClassIds = useMemo(() => new Set(myClasses.map((c) => c.id)), [myClasses]);

  // Load records
  const loadRecords = useCallback(async () => {
    setLoadingRecords(true);
    try {
      const snap = await getDocs(
        query(collection(db, COLLECTIONS.discipline), orderBy('createdAt', 'desc')),
      );
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() } as DisciplineRecord));
      // Class-scoped: only show records for classes this user can access
      const filtered = isPrivileged ? all : all.filter((r) => myClassIds.has(r.classId));
      setRecords(filtered);
    } catch (e) {
      console.error('Failed to load discipline records:', e);
    } finally {
      setLoadingRecords(false);
    }
  }, [isPrivileged, myClassIds]);

  useEffect(() => {
    if (!dataLoading) loadRecords();
  }, [dataLoading, loadRecords]);

  // Filtered list
  const filteredRecords = useMemo(() => {
    const q = search.toLowerCase();
    return records.filter((r) => {
      if (classFilter && r.classId !== classFilter) return false;
      if (categoryFilter && r.category !== categoryFilter) return false;
      if (severityFilter && r.severity !== severityFilter) return false;
      if (statusFilter && r.status !== statusFilter) return false;
      if (q && !r.studentName.toLowerCase().includes(q) && !r.description.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [records, search, classFilter, categoryFilter, severityFilter, statusFilter]);

  const stats = useMemo(() => {
    const open = records.filter((r) => r.status === 'open').length;
    const critical = records.filter((r) => r.severity === 'critical' || r.severity === 'high').length;
    const thisMonth = records.filter((r) => {
      const d = r.incidentDate || r.createdAt;
      return d?.startsWith(new Date().toISOString().slice(0, 7));
    }).length;
    return { total: records.length, open, critical, thisMonth };
  }, [records]);

  const className = (id: string) => classes.find((c) => c.id === id)?.name ?? id;
  const studentObj = (id: string) => students.find((s) => s.id === id);

  /* ---- OPEN FORM ------------------------------------------------ */

  function openNew() {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setMsg(null);
    setMode('form');
  }

  function openEdit(record: DisciplineRecord) {
    setEditingId(record.id);
    setForm({
      studentId: record.studentId,
      category: record.category,
      severity: record.severity,
      description: record.description,
      actionTaken: record.actionTaken ?? '',
      incidentDate: record.incidentDate,
      status: record.status,
      parentNotified: record.parentNotified ?? false,
      notes: record.notes ?? '',
    });
    setMsg(null);
    setMode('form');
  }

  /* ---- SAVE ----------------------------------------------------- */

  async function handleSave() {
    const errors: string[] = [];
    if (!form.studentId) errors.push('Select a student.');
    if (!form.description.trim()) errors.push('Description is required.');
    if (!form.incidentDate) errors.push('Incident date is required.');

    if (errors.length) {
      setMsg({ ok: false, text: errors.join(' ') });
      return;
    }

    const student = students.find((s) => s.id === form.studentId);
    if (!student) {
      setMsg({ ok: false, text: 'Student not found.' });
      return;
    }

    setSaving(true);
    setMsg(null);

    try {
      const now = new Date().toISOString();
      const actor = userData?.uid ?? 'system';
      const actorName = userData?.displayName ?? 'System';
      const cls = classes.find((c) => c.id === student.currentClass);

      const payload = stripUndefined({
        studentId: student.id,
        studentName: student.fullName,
        classId: student.currentClass,
        className: cls?.name ?? student.currentClass,
        category: form.category,
        severity: form.severity,
        description: sanitizeComment(form.description, 2000),
        actionTaken: sanitizeComment(form.actionTaken, 1000) || undefined,
        incidentDate: form.incidentDate,
        status: form.status,
        parentNotified: form.parentNotified,
        notes: sanitizeComment(form.notes, 1000) || undefined,
        updatedAt: now,
        updatedBy: actor,
      });

      if (editingId) {
        await updateDoc(doc(db, COLLECTIONS.discipline, editingId), payload);
        if (form.status === 'resolved') {
          await updateDoc(doc(db, COLLECTIONS.discipline, editingId), {
            resolvedAt: now,
            resolvedBy: actor,
          });
        }
        setMsg({ ok: true, text: 'Record updated.' });
        setSaveModal({ show: true, status: 'success', message: 'Discipline record updated.' });
        auditFromContext(userData, 'discipline_updated', 'discipline', { targetId: editingId, targetType: 'discipline', targetName: student.fullName, details: { category: form.category, severity: form.severity, status: form.status } });
      } else {
        await addDoc(collection(db, COLLECTIONS.discipline), stripUndefined({
          ...payload,
          recordedBy: actor,
          recordedByName: actorName,
          createdAt: now,
        }));
        setMsg({ ok: true, text: 'Discipline record added.' });
        setSaveModal({ show: true, status: 'success', message: 'Discipline record saved.' });
        auditFromContext(userData, 'discipline_created', 'discipline', { targetType: 'discipline', targetName: student.fullName, details: { category: form.category, severity: form.severity } });
        // Reset for another entry
        setForm({ ...EMPTY_FORM });
      }

      await loadRecords();
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  /* ---- DELETE ---------------------------------------------------- */

  async function handleDelete(id: string) {
    try {
      const rec = records.find((r) => r.id === id);
      await deleteDoc(doc(db, COLLECTIONS.discipline, id));
      auditFromContext(userData, 'discipline_deleted', 'discipline', {
        targetId: id, targetType: 'discipline',
        targetName: rec?.studentName, details: { category: rec?.category },
      });
      setRecords((rs) => rs.filter((r) => r.id !== id));
      setConfirmDelete(null);
      setMsg({ ok: true, text: 'Record deleted.' });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    }
  }

  /* ---- RENDER --------------------------------------------------- */

  if (dataLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner label="Loading…" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <GradientHero
        icon={ShieldAlert}
        title="Discipline"
        subtitle={
          mode === 'form'
            ? editingId ? 'Edit discipline record' : 'Record new incident'
            : `${stats.total} records · ${stats.open} open`
        }
        actions={
          mode === 'list' ? (
            <AccentButton icon={Plus} onClick={openNew}>
              Record Incident
            </AccentButton>
          ) : (
            <AccentButton icon={ChevronLeft} variant="soft" onClick={() => { setMode('list'); setMsg(null); }}>
              Back to List
            </AccentButton>
          )
        }
      />

      {msg && (
        <SoftCard>
          <div className={`flex items-center gap-2 text-sm ${msg.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
            {msg.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            {msg.text}
          </div>
        </SoftCard>
      )}

      {mode === 'list' ? renderList() : renderForm()}

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
    </div>
  );

  /* ================================================================
     LIST VIEW
     ================================================================ */

  function renderList() {
    return (
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatChip tone="blue" value={stats.total} label="Total Records" icon={ShieldAlert} />
          <StatChip tone="gold" value={stats.open} label="Open" icon={Clock} />
          <StatChip tone="red" value={stats.critical} label="High / Critical" icon={AlertTriangle} />
          <StatChip tone="emerald" value={stats.thisMonth} label="This Month" icon={Calendar} />
        </div>

        {/* Filters */}
        <SoftCard>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search student name or description…"
                  className={glassFieldClass('pl-10')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <select
                className={glassFieldClass('sm:w-44')}
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
              >
                <option value="">All Classes</option>
                {myClasses.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap gap-3">
              <select
                className={glassFieldClass('w-44')}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All Categories</option>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <select
                className={glassFieldClass('w-36')}
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
              >
                <option value="">All Severity</option>
                {SEVERITIES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <select
                className={glassFieldClass('w-36')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              {(classFilter || categoryFilter || severityFilter || statusFilter) && (
                <button
                  onClick={() => { setClassFilter(''); setCategoryFilter(''); setSeverityFilter(''); setStatusFilter(''); }}
                  className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
                  <X size={14} /> Clear filters
                </button>
              )}
            </div>
          </div>
        </SoftCard>

        {/* Records list */}
        {loadingRecords ? (
          <SoftCard><Spinner label="Loading records…" /></SoftCard>
        ) : (
          <div className="space-y-3">
            {filteredRecords.length === 0 && (
              <SoftCard>
                <p className="text-center text-gray-400 py-6">
                  {records.length === 0
                    ? 'No discipline records yet. Click "Record Incident" to add one.'
                    : 'No records match your filters.'}
                </p>
              </SoftCard>
            )}
            {filteredRecords.map((r) => {
              const sev = severityInfo(r.severity);
              const stu = studentObj(r.studentId);
              const StatusIcon = STATUSES.find((s) => s.value === r.status)?.icon ?? Clock;
              return (
                <SoftCard key={r.id} pad="p-4">
                  <div className="flex items-start gap-4">
                    {/* Student avatar */}
                    {stu && <StudentAvatar student={stu} size="sm" className="mt-0.5" />}

                    <div className="flex-1 min-w-0">
                      {/* Top row: name, class, date */}
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900 dark:text-white text-sm">
                          {r.studentName}
                        </span>
                        <span className="text-xs text-gray-400">{className(r.classId)}</span>
                        <span className="text-xs text-gray-400">·</span>
                        <span className="text-xs text-gray-500">{r.incidentDate}</span>
                      </div>

                      {/* Badges */}
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                          {categoryLabel(r.category)}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${sev.color}`}>
                          {sev.label}
                        </span>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                          r.status === 'resolved'
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                            : r.status === 'escalated'
                            ? 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                            : 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                        }`}>
                          <StatusIcon size={12} />
                          {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                        </span>
                        {r.parentNotified && (
                          <span className="text-xs text-gray-400">Parent notified</span>
                        )}
                      </div>

                      {/* Description */}
                      <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                        {r.description}
                      </p>
                      {r.actionTaken && (
                        <p className="text-xs text-gray-500 mt-1">
                          <span className="font-medium">Action:</span> {r.actionTaken}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => openEdit(r)}
                        className="text-xs text-[#2d5295] hover:text-[#1b3a6b] flex items-center gap-1"
                      >
                        <Pencil size={14} /> Edit
                      </button>
                      {isPrivileged && (
                        confirmDelete === r.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDelete(r.id)}
                              className="text-xs text-red-600 font-medium"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setConfirmDelete(null)}
                              className="text-xs text-gray-400"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDelete(r.id)}
                            className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1"
                          >
                            <Trash2 size={14} />
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </SoftCard>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  /* ================================================================
     FORM VIEW
     ================================================================ */

  function renderForm() {
    // Students the user can create records for (scoped to their classes)
    const availableStudents = students
      .filter((s) => isPrivileged || myClassIds.has(s.currentClass))
      .sort((a, b) => a.fullName.localeCompare(b.fullName));

    return (
      <div className="space-y-6">
        <SoftCard>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-5 flex items-center gap-2">
            <ShieldAlert size={20} className="text-[#2d5295]" />
            Incident Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Student *" icon={User} full>
              <select
                className={glassFieldClass()}
                value={form.studentId}
                onChange={(e) => setForm((f) => ({ ...f, studentId: e.target.value }))}
                disabled={!!editingId}
              >
                <option value="">Select student…</option>
                {availableStudents.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.fullName} — {className(s.currentClass)}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Category *">
              <select
                className={glassFieldClass()}
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as DisciplineCategory }))}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </Field>

            <Field label="Severity *">
              <select
                className={glassFieldClass()}
                value={form.severity}
                onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value as DisciplineSeverity }))}
              >
                {SEVERITIES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </Field>

            <Field label="Incident Date *" icon={Calendar}>
              <input
                type="date"
                className={glassFieldClass()}
                value={form.incidentDate}
                onChange={(e) => setForm((f) => ({ ...f, incidentDate: e.target.value }))}
              />
            </Field>

            <Field label="Status">
              <select
                className={glassFieldClass()}
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as DisciplineStatus }))}
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </Field>

            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.parentNotified}
                  onChange={(e) => setForm((f) => ({ ...f, parentNotified: e.target.checked }))}
                  className="rounded border-gray-300 text-[#2d5295] focus:ring-[#1b3a6b]"
                />
                Parent has been notified
              </label>
            </div>

            <Field label="Description *" full>
              <textarea
                className={glassFieldClass('min-h-[100px]')}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Describe the incident…"
              />
            </Field>

            <Field label="Action Taken" full>
              <textarea
                className={glassFieldClass('min-h-[80px]')}
                value={form.actionTaken}
                onChange={(e) => setForm((f) => ({ ...f, actionTaken: e.target.value }))}
                placeholder="e.g. Verbal warning, detention, parent called…"
              />
            </Field>

            <Field label="Additional Notes" full>
              <textarea
                className={glassFieldClass('min-h-[60px]')}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Any follow-up notes…"
              />
            </Field>
          </div>
        </SoftCard>

        <SoftCard>
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={() => { setMode('list'); setMsg(null); }}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ← Cancel
            </button>
            <AccentButton icon={Save} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Update Record' : 'Save Record'}
            </AccentButton>
          </div>
        </SoftCard>
      </div>
    );
  }
}

/* ==================================================================
   FIELD WRAPPER
   ================================================================== */

function Field({
  label,
  icon: Icon,
  children,
  full,
}: {
  label: string;
  icon?: typeof User;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon size={14} className="text-gray-400" />}
        {label}
      </label>
      {children}
    </div>
  );
}
